import React, { createContext, useContext, useEffect, useRef } from 'react'

import { connectionManager } from './connection-manager'

// Store active resource subscriptions
interface ResourceSubscription {
  resourceId: string
  subscribers: number
  lastActive: number
}

// Global registry of active resources
const activeResources = new Map<string, ResourceSubscription>()
const activeConnections = new Set<string>()

// Context for resource registry
interface ResourceRegistryContextValue {
  registerResource: (resourceType: string, resourceId: string) => void
  unregisterResource: (resourceType: string, resourceId: string) => void
  isResourceRegistered: (resourceType: string, resourceId: string) => boolean
  getActiveResources: (resourceType: string) => string[]
}

const ResourceRegistryContext =
  createContext<ResourceRegistryContextValue | null>(null)

// Provider component that manages resource registrations
export function ResourceRegistryProvider({
  children,
}: {
  children: React.ReactNode
}) {
  // Reference to track if mounted
  const isMountedRef = useRef(true)

  // Set up cleanup interval
  useEffect(() => {
    isMountedRef.current = true

    const interval = setInterval(() => {
      if (!isMountedRef.current) return

      const now = Date.now()

      // Check for inactive resources (no activity for 60 seconds)
      activeResources.forEach((subscription, key) => {
        if (
          now - subscription.lastActive > 60000 &&
          subscription.subscribers === 0
        ) {
          const [resourceType, resourceId] = key.split(':')
          activeResources.delete(key)
          activeConnections.delete(key)

          // Close the connection
          if (resourceType === 'thread') {
            connectionManager.closeResource(resourceId)
          } else if (resourceType === 'project') {
            // Use the appropriate connection manager from imported managers
            const managerName = `${resourceType}ConnectionManager`
            if (typeof window[managerName] === 'object') {
              window[managerName].closeResource(resourceId)
            }
          }
        }
      })
    }, 10000)

    return () => {
      isMountedRef.current = false
      clearInterval(interval)
    }
  }, [])

  // Methods to register/unregister resources
  const registerResource = (resourceType: string, resourceId: string) => {
    if (!resourceId) return

    const key = `${resourceType}:${resourceId}`

    // Only log the first time we register this resource
    if (!activeConnections.has(key)) {
      activeConnections.add(key)
    }

    // Update global tracking
    if (activeResources.has(key)) {
      const subscription = activeResources.get(key)!
      subscription.subscribers++
      subscription.lastActive = Date.now()
      activeResources.set(key, subscription)
    } else {
      activeResources.set(key, {
        resourceId,
        subscribers: 1,
        lastActive: Date.now(),
      })
    }
  }

  const unregisterResource = (resourceType: string, resourceId: string) => {
    if (!resourceId) return

    const key = `${resourceType}:${resourceId}`

    // Update global tracking
    if (activeResources.has(key)) {
      const subscription = activeResources.get(key)!
      subscription.subscribers--
      subscription.lastActive = Date.now()
      activeResources.set(key, subscription)
    }
  }

  const isResourceRegistered = (resourceType: string, resourceId: string) => {
    if (!resourceId) return false
    const key = `${resourceType}:${resourceId}`
    return activeConnections.has(key)
  }

  const getActiveResources = (resourceType: string) => {
    const resources: string[] = []
    activeConnections.forEach((key) => {
      if (key.startsWith(`${resourceType}:`)) {
        resources.push(key.split(':')[1])
      }
    })
    return resources
  }

  // Create stable context value
  const contextValue = React.useMemo(() => {
    return {
      registerResource,
      unregisterResource,
      isResourceRegistered,
      getActiveResources,
    }
  }, [])

  return (
    <ResourceRegistryContext.Provider value={contextValue}>
      {children}
    </ResourceRegistryContext.Provider>
  )
}

// Thread-specific hook for backward compatibility
export function useThreadRegistry(threadId: string | null) {
  const context = useContext(ResourceRegistryContext)

  if (!context) {
    console.error('useThreadRegistry used outside of ResourceRegistryProvider')
    // Return a default value instead of throwing
    return {
      isRegistered: false,
      activeThreads: [],
    }
  }

  // Register thread when component mounts
  useEffect(() => {
    if (threadId) {
      context.registerResource('thread', threadId)

      // Cleanup on unmount
      return () => {
        context.unregisterResource('thread', threadId)
      }
    }
  }, [threadId, context])

  // Return memoized value
  return React.useMemo(
    () => ({
      isRegistered: threadId
        ? context.isResourceRegistered('thread', threadId)
        : false,
      activeThreads: context.getActiveResources('thread'),
    }),
    [threadId, context]
  )
}

// Generic hook for any resource type
export function useResourceRegistry(
  resourceType: string,
  resourceId: string | null
) {
  const context = useContext(ResourceRegistryContext)

  if (!context) {
    console.error(
      `useResourceRegistry used outside of ResourceRegistryProvider`
    )
    return {
      isRegistered: false,
      activeResources: [],
    }
  }

  // Register resource when component mounts
  useEffect(() => {
    if (resourceId) {
      context.registerResource(resourceType, resourceId)

      return () => {
        context.unregisterResource(resourceType, resourceId)
      }
    }
  }, [resourceType, resourceId, context])

  return React.useMemo(
    () => ({
      isRegistered: resourceId
        ? context.isResourceRegistered(resourceType, resourceId)
        : false,
      activeResources: context.getActiveResources(resourceType),
    }),
    [resourceType, resourceId, context]
  )
}

// For backward compatibility
export { ResourceRegistryProvider as ThreadProvider }
